import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play } from 'lucide-react';
import heroMeshBg from '../images/generated-v2/hero-mesh-bg.png';
import desktopFacturas from '../images/desktop/screen-FACTURAS.png';
import iosFacturasList from '../images/ios-facturas-list.png';
import screenCreateFactura from '../images/screen-create-factura.png';
import iosInvoiceDemoGif from '../images/showcase/ios-invoice-demo.gif';
import { DOCS_WEB_APP_URL } from './constants';

export default function Hero({ onOpenDownload }) {
  return (
    <section className="relative overflow-hidden bg-ink-950">
      <img src={heroMeshBg} className="absolute inset-0 w-full h-full object-cover opacity-90" alt="" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-b from-ink-950/40 via-transparent to-ink-950" />
      <div className="absolute inset-0 grid-overlay opacity-40" />

      {/* NAV */}
      <nav className="relative z-20 px-6 lg:px-12 py-5 flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center shadow-lg shadow-brand-600/40">
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24" className="text-white">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <span className="text-white font-bold text-[17px] tracking-tight">Facturas Simples</span>
        </div>

        <div className="hidden md:flex items-center gap-1">
          <a href="#caracteristicas" className="text-white/70 hover:text-white text-[14px] font-medium px-3.5 py-2 rounded-lg hover:bg-white/10 transition">Producto</a>
          <a href="#precios" className="text-white/70 hover:text-white text-[14px] font-medium px-3.5 py-2 rounded-lg hover:bg-white/10 transition">Precios</a>
          <Link to="/documentacion" className="text-white/70 hover:text-white text-[14px] font-medium px-3.5 py-2 rounded-lg hover:bg-white/10 transition">Documentación</Link>
          <a href={DOCS_WEB_APP_URL} target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white text-[14px] font-medium px-3.5 py-2 rounded-lg hover:bg-white/10 transition">Manual</a>
          <Link to="/faq" className="text-white/70 hover:text-white text-[14px] font-medium px-3.5 py-2 rounded-lg hover:bg-white/10 transition">FAQ</Link>
        </div>

        <button
          onClick={onOpenDownload}
          className="bg-white text-ink-950 text-[14px] font-semibold px-4 py-2.5 rounded-lg hover:bg-ink-100 transition shadow-lg shadow-cyan-400/10 inline-flex items-center gap-1.5 cursor-pointer"
        >
          Descargar gratis
          <ArrowRight size={14} strokeWidth={2.5} />
        </button>
      </nav>

      {/* HERO CONTENT */}
      <div className="relative z-10 px-6 pt-20 pb-12 max-w-6xl mx-auto text-center">
        <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full border border-white/15 glass mb-9">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse-dot" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-white/95">
            Disponible en Android · iOS · Windows · macOS
          </span>
        </div>

        <h1 className="text-balance text-4xl md:text-6xl lg:text-[72px] font-bold leading-[1.04] tracking-tighter text-white mb-7">
          Facturación Electrónica<br />
          en El Salvador,<br />
          <span className="text-gradient-cyan">finalmente fácil y sin costo.</span>
        </h1>

        <p className="text-balance text-lg md:text-xl text-white/65 leading-relaxed max-w-2xl mx-auto mb-10">
          Emite los <span className="text-white">11 tipos de DTE</span>, firma con tu certificado{' '}
          <span className="text-white">.p12</span> y sincroniza con Hacienda en tiempo real. Desde tu celular, tu tablet o tu computadora.
        </p>

        <div className="flex flex-wrap items-center justify-center gap-3 mb-7">
          <button
            onClick={onOpenDownload}
            className="bg-white text-ink-950 text-[15px] font-semibold px-6 py-3.5 rounded-xl hover:bg-ink-100 transition inline-flex items-center gap-2 shadow-xl shadow-cyan-500/25 cursor-pointer"
          >
            Descargar gratis
            <ArrowRight size={16} strokeWidth={2.5} />
          </button>
          <a
            href="#capturas"
            className="glass text-white text-[15px] font-semibold px-6 py-3.5 rounded-xl hover:bg-white/15 transition inline-flex items-center gap-2"
          >
            <Play size={16} fill="currentColor" />
            Ver demo de 60 s
          </a>
        </div>

        <p className="text-[13px] text-white/45">
          Sin tarjeta de crédito  ·  11 tipos de DTE incluidos  ·  Listo en 5 minutos
        </p>
      </div>

      {/* DEVICE SHOWCASE */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 pb-24">
        <div className="relative">
          <div className="absolute -inset-x-12 -inset-y-12 bg-gradient-to-r from-brand-500/30 via-brand-cyan/30 to-brand-violet/30 blur-3xl opacity-50 rounded-full" />

          {/* Mac browser frame */}
          <div className="relative rounded-2xl overflow-hidden shadow-device border border-white/10 bg-ink-900">
            <div className="flex items-center gap-1.5 px-4 py-3 bg-ink-800/80 border-b border-white/5">
              <span className="w-3 h-3 rounded-full bg-rose-500/80" />
              <span className="w-3 h-3 rounded-full bg-amber-400/80" />
              <span className="w-3 h-3 rounded-full bg-emerald-500/80" />
              <div className="mx-auto px-3 py-1 rounded-md bg-ink-900/80 text-[11px] text-white/40 font-mono">
                facturas-simples.app  ·  Dashboard
              </div>
            </div>
            <img src={desktopFacturas} alt="Lista de documentos electrónicos en Windows" className="w-full block" />
          </div>

          {/* Phone 1 (left, iOS) */}
          <div className="hidden md:block absolute -left-8 -bottom-12 w-[180px] -rotate-6 animate-float-slow">
            <div className="rounded-[36px] bg-ink-900 p-2 shadow-device border border-white/10">
              <div className="rounded-[30px] overflow-hidden bg-black">
                <picture>
                  <source media="(min-width: 768px)" srcSet={iosInvoiceDemoGif} />
                  <img src={iosFacturasList} alt="Facturas en iPhone" className="w-full block" />
                </picture>
              </div>
            </div>
          </div>

          {/* Phone 2 (right, Android) */}
          <div className="hidden md:block absolute -right-8 -bottom-16 w-[200px] rotate-[5deg] animate-float-slow" style={{ animationDelay: '-3s' }}>
            <div className="rounded-[36px] bg-ink-900 p-2 shadow-device border border-white/10">
              <div className="rounded-[30px] overflow-hidden bg-black">
                <img src={screenCreateFactura} alt="Crear factura en Android" className="w-full block" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
